import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import Grid from "@/components/Grid";
import { CreateLoaderInput } from "@/components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps } from "@/pages/Holidays";
import {
  setStatusHolidays,
  getShowHolidays,
  deleteHolidaysData,
} from "@/Api/holidays";
import { HOLIDAY_TYPE_CONST } from "@/constants/HolidayTypeConst";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

const HOLIDAY_TYPE_LABELS: Record<number, string> = {
  [HOLIDAY_TYPE_CONST.NATIONAL]: "Nacional",
  [HOLIDAY_TYPE_CONST.STATE]: "Estadual",
  [HOLIDAY_TYPE_CONST.MUNICIPAL]: "Municipal",
  [HOLIDAY_TYPE_CONST.OPTIONAL]: "Facultativo",
};

const formatDate = (value?: string | null) =>
  value ? value.slice(0, 10).split("-").reverse().join("/") : "-";

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
}: TypeTableProps<TableDataProps>) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Nome</TableHead>
            <TableHead>Data</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.name}
                </TableCell>
                <TableCell>{formatDate(item?.date)}</TableCell>
                <TableCell>
                  {HOLIDAY_TYPE_LABELS[item?.holiday_type_id] || "-"}
                </TableCell>
                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={!!item?.is_active}
                    params={{
                      func: setStatusHolidays,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: !values?.is_active,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-holiday")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-holiday")}>
                      <DeleteBtn
                        params={{
                          func: deleteHolidaysData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-holiday-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Feriado" }}
                        params={{
                          func: getShowHolidays,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <div className="w-full h-auto">
                            <div className="flex flex-col gap-3">
                              <Grid.Item name="Nome" flex={0}>
                                {responseApi?.name}
                              </Grid.Item>
                              <Grid.Item name="Data" flex={0}>
                                {formatDate(responseApi?.date)}
                              </Grid.Item>
                              <Grid.Item name="Tipo" flex={0}>
                                {HOLIDAY_TYPE_LABELS[
                                  responseApi?.holiday_type_id
                                ] || "-"}
                              </Grid.Item>
                              <Grid.Item name="UF" flex={0}>
                                {responseApi?.state || "-"}
                              </Grid.Item>
                              <Grid.Item name="Cidade" flex={0}>
                                {responseApi?.city || "-"}
                              </Grid.Item>
                              <Grid.Item name="Recorrente" flex={0}>
                                {responseApi?.is_recurring_yearly
                                  ? "Sim"
                                  : "Não"}
                              </Grid.Item>
                            </div>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={5} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
